/**
 * Imported site-inspection reports — review, correct, apply
 * (budget-scope-of-work-draws.md § "Delete a draw + Import site inspection",
 * 2026-10-02).
 *
 *   GET  .../draws/{dealId}/inspections → { inspections: [row] }, newest first
 *   POST .../draws/{dealId}/inspections/{documentId}/apply
 *        { drawId? , …draw fields } → { draw, rollup }   (409 unless parsed)
 *
 * One card per report. `parsing` cards poll every ~10 s; `failed` /
 * `rejected` / `not_an_inspection` show the reason and no Apply. A parsed card
 * shows the report's facts, its line table (read-only AG Grid), the
 * `differences` against the linked draw as warnings, and a form pre-filled from
 * `proposedDrawFields` so the reviewer can correct a value before
 * "Apply to draw #N" (when tagged / linked) or "Create draw from this report".
 *
 * Renders nothing until the loan has at least one imported report.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";
import { useDispatch } from "react-redux";
import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";

import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../../../_src_shared/utils/functions/formatDateTime";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";
import AgGridTableWrapper from "../../../../components/agGrid/AgGridTableWrapper";
import {
  FormDateField,
  FormFieldGrid,
  FormMoneyField,
  FormPercentField,
  FormSelectField,
  FormTextField,
  FormWarn,
} from "../../../../components/forms/FormPrimitives";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import { useResolveLoanDocument } from "../LoanDrilldownWireAndFundingTab/panes/HudSourceChip";
import { EMPTY, T, cardSx, labelSx, money, pct } from "./budgetDrawsTheme";

const POLL_MS = 10000;

const STATUS = {
  parsing: { label: "Parsing…", bg: T.accentTint, fg: T.accentInk },
  parsed: { label: "Parsed", bg: T.greenTint, fg: T.green },
  not_an_inspection: { label: "Not an inspection", bg: T.amberTint, fg: T.amberInk },
  rejected: { label: "Rejected", bg: T.amberTint, fg: T.amberInk },
  failed: { label: "Failed", bg: "#FBEEEC", fg: T.red },
};

const FIELD_LABEL = {
  dateOfInspection: "Inspection date",
  inspectionVendor: "Vendor",
  percentComplete: "% complete",
  drawRequestedAmount: "Requested amount",
  drawNotes: "Notes",
};

const fmtDiff = (field, v) =>
  v == null || v === ""
    ? EMPTY
    : field === "percentComplete"
      ? pct(v, 1)
      : field === "drawRequestedAmount"
        ? money(v)
        : field === "dateOfInspection"
          ? formatDate(v, String(v))
          : String(v);

const StatusPill = ({ status }) => {
  const s = STATUS[status] || STATUS.parsing;
  return (
    <Box
      component="span"
      sx={{
        fontSize: 11.5,
        fontWeight: 700,
        px: 0.9,
        py: "2px",
        borderRadius: "99px",
        bgcolor: s.bg,
        color: s.fg,
        whiteSpace: "nowrap",
      }}
    >
      {s.label}
    </Box>
  );
};

const LINE_COLS = [
  { field: "name", headerName: "Line", flex: 1, minWidth: 180, tooltipField: "name" },
  { field: "budgetAmount", headerName: "Budget", width: 120, type: "numericColumn", valueFormatter: (p) => money(p.value) },
  { field: "previouslyFunded", headerName: "Previously funded", width: 140, type: "numericColumn", valueFormatter: (p) => money(p.value) },
  { field: "requestedAmount", headerName: "Requested", width: 120, type: "numericColumn", valueFormatter: (p) => money(p.value) },
  { field: "approvedAmount", headerName: "Approved", width: 120, type: "numericColumn", valueFormatter: (p) => money(p.value) },
  {
    field: "percentComplete",
    headerName: "% complete",
    width: 110,
    type: "numericColumn",
    // Printed 0-100 on the report.
    valueFormatter: (p) => (p.value == null ? EMPTY : `${Number(p.value)}%`),
  },
  { field: "remainingBalance", headerName: "Remaining", width: 120, type: "numericColumn", valueFormatter: (p) => money(p.value) },
];

/** One report. */
const InspectionCard = ({ row, draws, propertyOptions, multiProperty, onApply, applying }) => {
  const dispatch = useDispatch();
  const resolveDoc = useResolveLoanDocument();
  const [open, setOpen] = useState(!row.applied && row.status === "parsed");
  const insp = row.inspection || {};
  const proposed = row.proposedDrawFields || {};

  const [form, setForm] = useState(() => ({
    dateOfInspection: proposed.dateOfInspection ? String(proposed.dateOfInspection).slice(0, 10) : "",
    inspectionVendor: proposed.inspectionVendor || "",
    percentComplete: proposed.percentComplete ?? null,
    drawRequestedAmount: proposed.drawRequestedAmount ?? null,
    drawNotes: proposed.drawNotes || "",
    subjectPropertyId: "",
  }));
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const doc = resolveDoc({ documentId: row.documentId, fileStorageName: row.blobName });
  const linkedDraw = (draws || []).find((d) => String(d?._id || d?.oid) === String(row.drawId));
  const reason =
    row.status === "failed"
      ? row.error || "The report could not be read."
      : row.status === "rejected"
        ? row.rejection?.reason || "The extractor refused this document."
        : row.status === "not_an_inspection"
          ? "This document read as something other than a site inspection (an invoice, a wire, …)."
          : null;

  // Only the fields the reviewer filled are sent; each overrides the parsed value.
  const body = () => {
    const out = {};
    if (form.dateOfInspection) out.dateOfInspection = form.dateOfInspection;
    if (form.inspectionVendor) out.inspectionVendor = form.inspectionVendor;
    if (form.percentComplete != null) out.percentComplete = form.percentComplete;
    if (form.drawRequestedAmount != null) out.drawRequestedAmount = form.drawRequestedAmount;
    if (form.drawNotes) out.drawNotes = form.drawNotes;
    return out;
  };
  const createBlocked = multiProperty && !form.subjectPropertyId;

  return (
    <Box sx={{ border: `1px solid ${T.line}`, borderRadius: T.radiusSm, overflow: "hidden" }}>
      <Flx
        ac
        jb
        wrap
        sx={{ gap: 1, p: "10px 14px", bgcolor: T.subtle, cursor: "pointer" }}
        onClick={() => setOpen((o) => !o)}
      >
        <Flx ac sx={{ gap: 1, minWidth: 0 }}>
          <KeyboardArrowDownRounded
            sx={{
              fontSize: 18,
              color: T.muted,
              transition: "transform .15s",
              transform: open ? "none" : "rotate(-90deg)",
            }}
          />
          <Box
            component="button"
            type="button"
            disabled={!doc}
            onClick={(e) => {
              e.stopPropagation();
              if (doc) dispatch(filePreviewSetData(doc));
            }}
            sx={{
              border: "none",
              background: "transparent",
              p: 0,
              font: "inherit",
              fontSize: 13,
              fontWeight: 600,
              color: doc ? T.accentInk : T.ink,
              cursor: doc ? "pointer" : "default",
              textAlign: "left",
              "&:hover": doc ? { textDecoration: "underline" } : {},
            }}
          >
            {row.fileName || row.blobName || "Inspection report"}
          </Box>
          <StatusPill status={row.status} />
          {row.applied ? (
            <Txt sx={{ fontSize: 12, fontWeight: 600, color: T.green }}>
              Applied to draw #{row.drawNumber}
            </Txt>
          ) : row.drawNumber ? (
            <Txt sx={{ fontSize: 12, color: T.muted }}>Tagged to draw #{row.drawNumber}</Txt>
          ) : null}
        </Flx>
        <Txt sx={{ fontSize: 12, color: T.faint }}>
          {[row.uploadedBy, row.uploadDate ? formatDateTime(row.uploadDate, "datetime") : null]
            .filter(Boolean)
            .join(" · ")}
        </Txt>
      </Flx>

      {open ? (
        <Box sx={{ p: "14px 16px 16px" }}>
          {row.status === "parsing" ? (
            <Flx ac sx={{ gap: 1 }}>
              <CircularProgress size={14} />
              <Txt sx={{ fontSize: 12.5, color: T.muted }}>
                Reading the report — this refreshes every few seconds.
              </Txt>
            </Flx>
          ) : reason ? (
            <FormWarn>{reason}</FormWarn>
          ) : (
            <Flx column sx={{ gap: 2 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                  gap: 2,
                }}
              >
                {[
                  ["Vendor", insp.inspectionVendor],
                  ["Inspected", insp.dateOfInspection ? formatDate(insp.dateOfInspection, EMPTY) : null],
                  ["% complete", insp.percentComplete != null ? `${Number(insp.percentComplete)}%` : null],
                  ["Recommended release", insp.recommendedDisbursement != null ? money(insp.recommendedDisbursement) : null],
                  ["Revised budget", insp.revisedBudgetTotal != null ? money(insp.revisedBudgetTotal) : null],
                  ["Draw # on report", row.drawNumberOnReport],
                  ["Property on report", row.propertyAddressOnReport],
                ].map(([k, v]) => (
                  <Flx column key={k} sx={{ gap: 0.25, minWidth: 0 }}>
                    <Txt sx={{ ...labelSx, display: "block" }}>{k}</Txt>
                    <Txt sx={{ fontSize: 12.5, color: v ? T.ink : T.faint, wordBreak: "break-word" }}>
                      {v || EMPTY}
                    </Txt>
                  </Flx>
                ))}
              </Box>
              {insp.workCompletedSummary ? (
                <Txt sx={{ fontSize: 12.5, color: T.ink2, display: "block", lineHeight: 1.5 }}>
                  {insp.workCompletedSummary}
                </Txt>
              ) : null}

              {(insp.lineItems || []).length ? (
                <AgGridTableWrapper
                  height={`${Math.min((insp.lineItems.length + 1) * 36 + 16, 320)}px`}
                  noBorder
                >
                  <AgGridReact
                    rowData={insp.lineItems}
                    columnDefs={LINE_COLS}
                    defaultColDef={{ resizable: true, sortable: true }}
                    tooltipShowDelay={300}
                  />
                </AgGridTableWrapper>
              ) : null}

              {(row.differences || []).length ? (
                <FormWarn>
                  <b>
                    The report differs from draw #{row.drawNumber ?? linkedDraw?.drawNumber}:
                  </b>{" "}
                  {row.differences
                    .map(
                      (d) =>
                        `${FIELD_LABEL[d.field] || d.field} — report ${fmtDiff(d.field, d.inspection)}, draw ${fmtDiff(d.field, d.draw)}`,
                    )
                    .join("; ")}
                </FormWarn>
              ) : null}

              <Box>
                <Txt sx={{ ...labelSx, display: "block", mb: 1 }}>
                  What Apply writes — correct anything before applying
                </Txt>
                <FormFieldGrid>
                  <FormDateField
                    label="Inspection date"
                    value={form.dateOfInspection}
                    onChange={set("dateOfInspection")}
                    span={2}
                  />
                  <FormTextField
                    label="Vendor"
                    value={form.inspectionVendor}
                    onChange={set("inspectionVendor")}
                    span={2}
                  />
                  <FormPercentField
                    label="% complete"
                    value={form.percentComplete}
                    onChange={set("percentComplete")}
                    span={2}
                  />
                  <FormMoneyField
                    label="Requested amount (the inspector's recommended release)"
                    value={form.drawRequestedAmount}
                    onChange={set("drawRequestedAmount")}
                    span={3}
                  />
                  {multiProperty ? (
                    <FormSelectField
                      label="Property (for a new draw)"
                      value={form.subjectPropertyId}
                      onChange={set("subjectPropertyId")}
                      options={propertyOptions}
                      span={3}
                    />
                  ) : null}
                  <FormTextField
                    label="Notes"
                    value={form.drawNotes}
                    onChange={set("drawNotes")}
                    helperText="Never overwrites notes already on the draw."
                    multiline
                    minRows={2}
                  />
                </FormFieldGrid>
              </Box>

              <Flx ac wrap sx={{ gap: 1, justifyContent: "flex-end" }}>
                {applying ? <CircularProgress size={16} /> : null}
                <Button
                  variant="outlined"
                  size="small"
                  disabled={applying || createBlocked}
                  title={createBlocked ? "Choose the property first" : ""}
                  onClick={() =>
                    onApply(row, {
                      ...body(),
                      ...(form.subjectPropertyId ? { subjectPropertyId: form.subjectPropertyId } : {}),
                    })
                  }
                >
                  Create draw from this report
                </Button>
                {row.drawId ? (
                  <Button
                    variant="contained"
                    size="small"
                    disabled={applying}
                    onClick={() => onApply(row, { drawId: row.drawId, ...body() })}
                  >
                    Apply to draw #{row.drawNumber ?? linkedDraw?.drawNumber}
                  </Button>
                ) : null}
              </Flx>
            </Flx>
          )}
        </Box>
      ) : null}
    </Box>
  );
};

const InspectionsPanel = ({ api, dealId, draws, properties, refreshKey, onApplied }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [rows, setRows] = useState([]);
  const [applyingId, setApplyingId] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async () => {
    if (!dealId) return;
    try {
      const res = await api.getInspections(dealId);
      setRows(Array.isArray(res?.inspections) ? res.inspections : []);
    } catch {
      // Not served yet / no access: the panel just stays hidden.
    }
  }, [api, dealId]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // Poll every ~10 s while any report is still being read.
  const anyParsing = rows.some((r) => r.status === "parsing");
  useEffect(() => {
    clearTimeout(timer.current);
    if (anyParsing) timer.current = setTimeout(load, POLL_MS);
    return () => clearTimeout(timer.current);
  }, [anyParsing, rows, load]);

  const propertyOptions = useMemo(
    () =>
      (properties || []).map((p) => ({
        value: String(p?._id || p?.oid),
        label: addressLabel(p?.address) || addressLabel(p?.propertyAddress) || "Property",
      })),
    [properties],
  );

  const handleApply = useCallback(
    async (row, body) => {
      setApplyingId(row.documentId);
      try {
        const res = await api.applyInspection(dealId, row.documentId, body);
        enqueueSnackbar(
          body.drawId
            ? `Applied to draw #${res?.draw?.drawNumber ?? row.drawNumber}`
            : `Draw #${res?.draw?.drawNumber ?? ""} created from the report`,
          { variant: "success" },
        );
        await load();
        onApplied?.();
      } catch (err) {
        enqueueSnackbar(
          err?.response?.data?.detail || err?.message || "Could not apply the report.",
          { variant: "error" },
        );
      } finally {
        setApplyingId(null);
      }
    },
    [api, dealId, enqueueSnackbar, load, onApplied],
  );

  if (!rows.length) return null;

  return (
    <Box sx={cardSx}>
      <Flx jb ac wrap sx={{ gap: 1.5, p: "16px 20px", borderBottom: `1px solid ${T.line2}` }}>
        <Flx ac sx={{ gap: 1.5 }}>
          <Txt sx={{ fontSize: 15.5, fontWeight: 700, color: T.ink, letterSpacing: "-0.01em" }}>
            Imported inspections
          </Txt>
          <Txt sx={{ fontSize: 12, color: T.muted }}>
            {rows.length} report{rows.length === 1 ? "" : "s"}
            {anyParsing ? " · reading…" : ""}
          </Txt>
        </Flx>
        <Txt sx={{ fontSize: 12, color: T.faint }}>
          Review each report, correct what Apply would write, then apply it to its draw.
        </Txt>
      </Flx>
      <Flx column sx={{ gap: 1.25, p: "14px 20px 18px" }}>
        {rows.map((r) => (
          <InspectionCard
            key={`${r.documentId}-${r.status}-${r.applied}`}
            row={r}
            draws={draws}
            propertyOptions={propertyOptions}
            multiProperty={(properties || []).length > 1}
            onApply={handleApply}
            applying={applyingId === r.documentId}
          />
        ))}
      </Flx>
    </Box>
  );
};

InspectionsPanel.propTypes = {
  api: PropTypes.object.isRequired,
  dealId: PropTypes.string,
  draws: PropTypes.array,
  properties: PropTypes.array,
  refreshKey: PropTypes.number,
  onApplied: PropTypes.func,
};

export default InspectionsPanel;
